import { FaSolidCopy, FaSolidRotate, FaSolidTrash } from "solid-icons/fa"
import { createEffect, createSignal, onMount, useContext, type Component } from "solid-js"
import type { Object3D } from "three"
import type { Nullable } from "vite-node"
import type { Engine } from "../../../../lib/core/engine/Engine.type"
import { HorizontalOrVertical } from "../../../../lib/core/entity/EntityHeading/HorizontalOrVertical.enum"
import isEntityLayerIdTerrain1 from "../../../../lib/core/entity/EntityLayerId/isEntityLayerIdTerrain1"
import { EntityTypeId } from "../../../../lib/core/entity/EntityTypeId.enum"
import entityTypeMetaList from "../../../../lib/core/entity/entityTypeMetaList.generated"
import type { EntityTypeMeta } from "../../../../lib/core/EntityTypeMeta"
import { createRownipulatorDeleteRowAction } from "../../../../lib/core/state/flux/action/Map/RownipulatorDeleteRowAction"
import { createRownipulatorDuplicateRowAction } from "../../../../lib/core/state/flux/action/Map/RownipulatorDuplicateRowAction"
import dispatchClient from "../../../../lib/core/state/flux/dispatchClient"
import type { TilePositionXY } from "../../../../lib/core/tile_position_xy/TilePositionXY.type"
import { EventResult } from "../../../../lib/event/event"
import { InputActionType } from "../../../../lib/event/InputRouter/InputActionType"
import KeyboardShortcut from "../../../../lib/event/InputRouter/KeyboardShortcut.component"
import { mountInputActionListener, mountInputActionListenerWhen } from "../../../../lib/event/InputRouter/mountInputActionListener"
import Button from "../../../../lib/jsx/Button"
import EngineContext from "../../../EngineContext"
import SelectEntityTypeMetaDropDownButton from "../../../SelectEntityTypeMetaDropDownButton"
import createAddRowGroup from "./createAddRowGroup"
import createEditRowGroup from "./createEditRowGroup"

const RownipulatorTool: Component = () => {
  const engine: Engine = useContext(EngineContext)

  const [getSelectedLayer1Etm, setSelectedLayer1Etm] = createSignal<Nullable<EntityTypeMeta>>(null)
  const [getSelectedRowPosition, setSelectedRowPosition] = createSignal<TilePositionXY | null>(null)
  // Orientation signal
  const [getOrientation, setOrientation] = createSignal<HorizontalOrVertical>(HorizontalOrVertical.Vertical)
  createEffect(() => {
    console.log('orientation', getOrientation() === HorizontalOrVertical.Horizontal ? 'horizontal' : 'vertical')
  })

  const interactiveObjects: Object3D[] = []

  onMount(() => {
    setSelectedLayer1Etm(entityTypeMetaList.find(etm => etm.id === EntityTypeId.Plain))
  })

  createAddRowGroup(engine, getSelectedLayer1Etm, interactiveObjects)
  createEditRowGroup(engine, interactiveObjects,
    getSelectedRowPosition, setSelectedRowPosition,
    getOrientation, setOrientation)

  // eslint-disable-next-line solid/reactivity
  mountInputActionListenerWhen(InputActionType.Escape, getSelectedRowPosition, () => {
    setSelectedRowPosition(null)
    return EventResult.Stop
  })

  mountInputActionListener(InputActionType.RotateClockwise, () => {
    toggleOrientation()
    return EventResult.Stop
  })

  // eslint-disable-next-line solid/reactivity
  mountInputActionListenerWhen(InputActionType.Delete, getSelectedRowPosition, () => {
    deleteRow()
    return EventResult.Stop
  })

  // eslint-disable-next-line solid/reactivity
  mountInputActionListenerWhen(InputActionType.Duplicate, getSelectedRowPosition, () => {
    duplicateRow()
    return EventResult.Stop
  })

  function toggleOrientation() {
    setOrientation(
      getOrientation() === HorizontalOrVertical.Horizontal
        ? HorizontalOrVertical.Vertical
        : HorizontalOrVertical.Horizontal
    )
  }

  function duplicateRow() {
    dispatchClient(engine, createRownipulatorDuplicateRowAction(getSelectedRowPosition()!, getOrientation()))
  }
  function deleteRow() {
    dispatchClient(engine, createRownipulatorDeleteRowAction(getSelectedRowPosition()!, getOrientation()))
  }

  return <div>
    ResizeTool
    <hr />
    <table class="table table-sm">
      <tbody>
        <tr>
          <td class="text-muted">Width</td>
          <td class="text-end">{engine.state.width}</td>
        </tr>
        <tr>
          <td class="text-muted">Height</td>
          <td class="text-end">{engine.state.height}</td>
        </tr>
      </tbody>
    </table>
    <hr />
    <SelectEntityTypeMetaDropDownButton
      value={getSelectedLayer1Etm()}
      onChange={(event) => {
        console.log('onChange', event)
        setSelectedLayer1Etm(event)
      }}
      list={entityTypeMetaList.filter(isEntityLayerIdTerrain1)}
      playerId={null}
      placeholder="Select Terrain"
    />
    <hr />
    <div class="btn-group-vertical">
      <Button class="btn btn-secondary" onClick={toggleOrientation} ariaLabel="Toggle Horizontal or Vertical">
        <FaSolidRotate /> {getOrientation() === HorizontalOrVertical.Horizontal ? 'Horizontal' : 'Vertical'} <KeyboardShortcut action={InputActionType.RotateClockwise} />
      </Button>
      <Button class="btn btn-secondary" disabled={!getSelectedRowPosition()} onClick={duplicateRow} ariaLabel="Duplicate Row">
        <FaSolidCopy /> Duplicate Row <KeyboardShortcut action={InputActionType.Duplicate} />
      </Button>
      <Button class="btn btn-danger" disabled={!getSelectedRowPosition()} onClick={deleteRow} ariaLabel="Delete Row">
        <FaSolidTrash /> Delete Row <KeyboardShortcut action={InputActionType.Delete} />
      </Button>
    </div>
  </div>
}

export default RownipulatorTool
